import { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Row,
  Button,
  Input,
  Label,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { tickets, ticketsManagement } from "../../helpers/backend_helper";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDivisions,
  fetchRanges2,
  fetchRoles,
  fetchRounds,
  getAllUSer,
} from "../../store/actions";
import { useNavigate } from "react-router-dom";
import { formatDateOnly } from "../../helpers/formatDate";
import {
  checkFilterMatch,
  getDeviceIcon,
  getStatusBadge,
  maskIndianPhoneNumber,
} from "../../helpers/general";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../components/Common/Breadcrumbs";
import config from "../../config";

export default function UserHistory() {
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageType, setMessageType] = useState("whatsapp"); // 'whatsapp' or 'sms'
  const [singleUser, setSingleUser] = useState(null);
  const [divisionFilter, setDivisionFilter] = useState("");
  const [rangeFilter, setRangeFilter] = useState("");
  const [roundFilter, setRoundFilter] = useState("");
  
  // Add state to track if filters have been applied
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roles, allUsersnvehicle, divisions, ranges, rounds } = useSelector(
    (state) => state.Common || {}
  );

  // This function is explicitly defined in the component scope
  const goToTicketDetails = (e, ticketId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!ticketId) {
      toast.error("Invalid ticket ID", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setLoading(true);
    const payload = {
      act: "ticket",
      subact: "get",
      info: { ticketId },
    };
    ticketsManagement(payload)
      .then((response) => {
        setLoading(false);
        if (response?.message) {
          navigate(`/update-ticket?id=${ticketId}`, {
            state: {
              ticketData: response.data,
              editable: true,
            },
          });
        } else {
          toast.error("Failed to fetch ticket details", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching ticket details:", error);
        toast.error("Error fetching ticket details", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  // Fetch ticket history by user ID
  const fetchTicketHistory = async (userId) => {
    setActivityLoading(true);
    setSelectedTicket(userId);

    const payload = {
      act: "ticket",
      subact: "userActivity",
      info: {
        userId: userId,
      },
    };

    try {
      const response = await tickets(payload);
      if (response?.data) {
        setTicketHistory(response.data);
      }
    } catch (error) {
      toast.error("Error fetching user activity", {
        position: "top-right",
        autoClose: 3000,
      });
      setTicketHistory([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Apply filters - Updated to set filteredReports state
  const handleFilterApply = async () => {
    setLoading(true);
    try {
      let filtered = allUsersnvehicle || [];

      // Text search (name) - keep your existing logic
      if (searchText && searchText !== "") {
        const searchLower = searchText.trimStart().toLowerCase();

        filtered = filtered.filter((user) => {
          const first = user.firstName?.trim()?.toLowerCase() || "";
          const last = user.lastName?.trim()?.toLowerCase() || "";
          const fullName = `${first} ${last}`.trim();

          return (
            first.includes(searchLower) ||
            last.includes(searchLower) ||
            fullName.includes(searchLower)
          );
        });
      }

      // Apply all other filters using checkFilterMatch
      filtered = filtered.filter((row) => {
        // Role filter
        if (roleFilter) {
          const userRole = row["regionId#roleId#userId"]?.split("#")[1];
          const role = roles?.find((role) => role.roleId == userRole);
          if (!role || !checkFilterMatch(roleFilter, role.roleId)) return false;
        }

        // Division filter - check both ID and name
        if (divisionFilter) {
          const divisionMatch = checkFilterMatch(divisionFilter, row.divisionId) || 
                               checkFilterMatch(divisionFilter, row.divisionName);
          if (!divisionMatch) return false;
        }

        // Range filter - check both ID and name
        if (rangeFilter) {
          const rangeMatch = checkFilterMatch(rangeFilter, row.rangeId) || 
                            checkFilterMatch(rangeFilter, row.rangeName);
          if (!rangeMatch) return false;
        }

        // Round filter - check both ID and name
        if (roundFilter) {
          const roundMatch = checkFilterMatch(roundFilter, row.roundId) || 
                            checkFilterMatch(roundFilter, row.roundName);
          if (!roundMatch) return false;
        }

        return true;  
      });

      setFilteredReports(filtered);
      setFiltersApplied(true); // Mark that filters have been applied
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Error applying filters", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    dispatch(getAllUSer());
    dispatch(fetchRoles());
    dispatch(fetchDivisions());
  }, []);

  // Fetch ranges when division filter changes
  useEffect(() => {
    if (divisionFilter) {
      dispatch(fetchRanges2(Number(divisionFilter))); // Fetch ranges based on division filter
      // Reset range and round filters when division changes
      setRangeFilter("");
      setRoundFilter("");
    } else {
      // Clear ranges when no division is selected
      setRangeFilter("");
      setRoundFilter("");
    }
  }, [divisionFilter]);

  // Fetch rounds when range filter changes
  useEffect(() => {
    if (rangeFilter) {
      dispatch(fetchRounds(Number(rangeFilter))); // Fetch rounds based on range filter
      // Reset round filter when range changes
      setRoundFilter("");
    } else {
      // Clear rounds when no range is selected
      setRoundFilter("");
    }
  }, [rangeFilter]);

  // Render users table
  const renderReportsTable = () => {
    const customStyles = {
      headCells: {
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          backgroundColor: "#f8f9fa",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
      rows: {
        style: {
          fontSize: "13px",
          "&:hover": {
            backgroundColor: "#f6f9fc",
          },
        },
      },
    };

    // Updated getFilteredData function - only return filtered data if filters have been applied
    const getFilteredData = () => {
      if (filtersApplied && filteredReports.length >= 0) {
        return filteredReports;
      }
      // If no filters applied, return all users
      return allUsersnvehicle || [];
    };

    const filteredData = getFilteredData();

    // Enhanced reset filters function
    const handleResetFilters = () => {
      setSearchText("");
      setRoleFilter("");
      setDivisionFilter("");
      setRangeFilter("");
      setRoundFilter("");
      setFilteredReports([]);
      setSelectedUsers([]);
      setSelectAll(false);
      setFiltersApplied(false); // Reset the filter applied state
    };

    // Handle select all checkbox
    const handleSelectAll = (e) => {
      const isChecked = e.target.checked;
      setSelectAll(isChecked);

      if (isChecked) {
        const allUserIds = filteredData.map((user) => user.userId);
        setSelectedUsers(allUserIds);
      } else {
        setSelectedUsers([]);
      }
    };

    // Handle individual user selection
    const handleUserSelect = (userId) => {
      setSelectedUsers((prev) => {
        const newSelection = prev.includes(userId)
          ? prev.filter((id) => id !== userId)
          : [...prev, userId];

        // Update select all checkbox state
        setSelectAll(newSelection.length === filteredData.length);

        return newSelection;
      });
    };

    // Export table data to CSV
    const handleExportData = () => {
      try {
        const dataToExport = filteredData.map((row) => {
          const firstName = row.firstName
            ? row.firstName.trim().charAt(0).toUpperCase() +
              row.firstName.slice(1)
            : "";
          const lastName = row.lastName
            ? row.lastName.trim().charAt(0).toUpperCase() +
              row.lastName.slice(1)
            : "";
          const userRole = row["regionId#roleId#userId"]?.split("#")[1];
          const role = roles?.find((role) => role.roleId == userRole);
          const rawNumber = row.contactNumber
            ? String(row.contactNumber).replace(/\D/g, "")
            : "";
          const battery = row.info?.currentLocation?.battery;
          const timestamp = row.info?.currentLocation?.updatedAt;

          return {
            "User Name": `${firstName} ${lastName}`,
            Email: row.email || "-",
            Location: row.info?.currentLocation?.isLocationOn ? "On" : "Off",
            Role: role ? role.role : "-",
            Division: row.divisionName || "-",
            Range: row.rangeName || "-",
            Round: row.roundName || "-",
            Phone: rawNumber ? maskIndianPhoneNumber(rawNumber) : "-",
            "App Version": row?.deviceInfo
              ? `${row?.deviceInfo?.version}+${row?.deviceInfo?.buildNumber}`
              : "-",
            Battery: battery != null ? `${battery}%` : "-",
            Device: row?.deviceInfo?.os || "-",
            "Device Brand": `${row?.deviceInfo?.brand || "-"} ${row?.deviceInfo?.versionDevice || ""}`.trim(),
            "Last Active": timestamp ? formatDateOnly(timestamp) : "-",
          };
        });

        if (dataToExport.length === 0) {
          toast.error("No data to export", {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }

        // Convert to CSV with proper formatting
        const headers = Object.keys(dataToExport[0] || {});
        const csvContent = [
          headers.map((h) => `"${h}"`).join(","),
          ...dataToExport.map((row) =>
            headers
              .map((header) => {
                let value = row[header] || "";
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                if (typeof value === "string") {
                  value = value.replace(/"/g, '""');
                  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
                    value = `"${value}"`;
                  }
                }
                return value;
              })
              .join(",")
          ),
        ].join("\n");

        // Create blob and download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;BOM" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `user-logs-${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success(`Exported ${dataToExport.length} records successfully`, {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error("Error exporting data:", error);
        toast.error("Failed to export data", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };

    // Open message modal for selected users
    const openMessageModal = (user = null) => {
      setSingleUser(user);
      setShowMessageModal(true);
      setMessageText("");
    };

    // Send message function
    const sendMessage = async () => {
      try {
        const usersToMessage = singleUser
          ? [singleUser]
          : filteredData.filter((user) => selectedUsers.includes(user.userId));

        const phoneNumbers = usersToMessage
          .map((user) => {
            const rawNumber = user.contactNumber
              ? String(user.contactNumber).replace(/\D/g, "")
              : "";
            return rawNumber;
          })
          .filter((number) => number);

        if (phoneNumbers.length === 0) {
          alert("No valid phone numbers found for selected users");
          return;
        }

        const message = {
          phoneNumbers,
          message: messageText,
        };

        const url =
          messageType === "whatsapp"
            ? config.REACT_APP_WHATSAPP_URL
            : config.REACT_APP_SMS_URL;

        const payload = {
          act: messageType,
          templateName: "cust_msg",
          phoneNumbers: message.phoneNumbers,
          params:[`${message.message}`],
        };

        const payload1 = {
          act: messageType,
          templateName: "cust_msg",
          phoneNumbers: message.phoneNumbers,
          params:[`${message.message}`],
        };

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(messageType === "whatsapp" ? payload : payload1),
        });

        const result = await response.json();

        if (result?.Result === "OK") {
          toast.success("Message sent successfully", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          console.error("Failed to send message:", message);
          toast.error("Failed to send message", {
            position: "top-right",
            autoClose: 3000,
          });
        }

        setShowMessageModal(false);
        setSelectedUsers([]);
        setSelectAll(false);
        setSingleUser(null);
        setMessageText("");
      } catch (error) {
        console.error("Error sending message:", error);
        alert("Failed to send message. Please try again.");
      }
    };

    // Add Range and Round columns to the existing columns array
    const columns = [
      {
        name: (
          <div className="d-flex align-items-center">
            <Input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              className="me-2"
            />
          </div>
        ),
        width: "2%",
        cell: (row) => (
          <Input
            type="checkbox"
            checked={selectedUsers.includes(row.userId)}
            onChange={() => handleUserSelect(row.userId)}
          />
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      {
        name: "User Name",
        selector: (row) => {
          const firstName = row.firstName
            ? row.firstName.trim().charAt(0).toUpperCase() +
              row.firstName.slice(1)
            : "";
          const lastName = row.lastName
            ? row.lastName.trim().charAt(0).toUpperCase() +
              row.lastName.slice(1)
            : "";
          return `${firstName} ${lastName}`;
        },
        sortable: true,
        width: "14%",
        cell: (row) => (
          <div className="d-flex align-items-center">
            <div>
              <span className="fw-medium">
                {row.firstName?.trim().charAt(0).toUpperCase() +
                  (row.firstName?.slice(1) || "")}{" "}
                {row.lastName?.trim().charAt(0).toUpperCase() +
                  (row.lastName?.slice(1) || "")}
              </span>
              <div className="small text-muted">{row.email || "-"}</div>
            </div>
          </div>
        ),
      },
      {
        name: "Location",
        selector: (row) =>
          row.info?.currentLocation?.isLocationOn ? "On" : "Off",
        width: "7%",
        sortable: true,
        cell: (row) => (
          <Badge
            color={
              row.info?.currentLocation?.isLocationOn ? "success" : "danger"
            }
            className="rounded-pill"
          >
            {row.info?.currentLocation?.isLocationOn ? "On" : "Off"}
          </Badge>
        ),
      },
      {
        name: "Role",
        selector: (row) => {
          const userRole = row["regionId#roleId#userId"]?.split("#")[1];
          const role = roles?.find((role) => role.roleId == userRole);
          return role ? role.role : "-";
        },
        width: "8%",
        sortable: true,
      },
      {
        name: "Division",
        selector: (row) => row.divisionName || "-",
        width: "7%",
        sortable: true,
        cell: (row) => (
          <span className="text-capitalize">{row.divisionName || "-"}</span>
        ),
      },
      {
        name: "Range",
        selector: (row) => row.rangeName || "-",
        width: "7%",
        sortable: true,
        cell: (row) => (
          <span className="text-capitalize">{row.rangeName || "-"}</span>
        ),
      },
      {
        name: "Round",
        selector: (row) => row.roundName || "-",
        width: "7%",
        sortable: true,
        cell: (row) => (
          <span className="text-capitalize">{row.roundName || "-"}</span>
        ),
      },
      {
        name: "Phone",
        selector: (row) => {
          const rawNumber = row.contactNumber
            ? String(row.contactNumber).replace(/\D/g, "")
            : "";
          return rawNumber ? maskIndianPhoneNumber(rawNumber) : "-";
        },
        width: "9%",
        sortable: true,
        cell: (row) => {
          const rawNumber = row.contactNumber
            ? String(row.contactNumber).replace(/\D/g, "")
            : "";
          const masked = rawNumber ? maskIndianPhoneNumber(rawNumber) : "-";
          return rawNumber ? (
            <a
              href={`tel:${rawNumber}`}
              className="text-primary text-decoration-none"
            >
              <i className="mdi mdi-phone me-1"></i>
              {masked}
            </a>
          ) : (
            "-"
          );
        },
      },
      {
        name: "App Version",
        selector: (row) =>
          row?.deviceInfo
            ? `${row?.deviceInfo?.version}+${row?.deviceInfo?.buildNumber}`
            : "-",
        width: "8%",
        sortable: true,
        cell: (row) => {
          const version = row?.deviceInfo?.version;
          const buildNumber = row?.deviceInfo?.buildNumber;
          return version ? (
            <span className="badge bg-light text-dark">
              {version}
              {buildNumber ? `+${buildNumber}` : ""}
            </span>
          ) : (
            "-"
          );
        },
      },
      {
        name: "Battery",
        selector: (row) => row.info?.currentLocation?.battery ?? null, // Fallback to null
        sortable: true,
        width: "6%",
        cell: (row) => {
          const battery = row.info?.currentLocation?.battery;
          return battery != null ? (
            <div>
              <span className="mdi mdi-battery-charging"></span> {battery}%
            </div>
          ) : (
            <span>-</span>
          );
        },
      },
      {
        name: "Device",
        selector: (row) => row?.deviceInfo?.os || "-",
        width: "9%",
        sortable: true,
        cell: (row) => {
          const os = row?.deviceInfo?.os;
          const icon = getDeviceIcon(os);
          return os ? (
            <span>
              <i className={`mdi ${icon} me-1`}></i>
              {os}

              <div>
                <em>
                  {row?.deviceInfo?.brand} {row?.deviceInfo?.versionDevice}
                </em>
              </div>
            </span>
          ) : (
            "-"
          );
        },
      },
      {
        name: "Last Active",
        selector: (row) => {
          const timestamp = row.info?.currentLocation?.updatedAt;
          return timestamp ? new Date(timestamp).getTime() : 0;
        },
        sortable: true,
        width: "10%",
        cell: (row) => {
          const timestamp = row.info?.currentLocation?.updatedAt;
          return timestamp ? (
            <div>
              <i className="mdi mdi-clock-outline me-1 text-muted"></i>
              {formatDateOnly(timestamp)}
            </div>
          ) : (
            "-"
          );
        },
      },
      {
        name: "Actions",
        width: "12%",
        cell: (row) => (
          <div className="d-flex gap-1">
            <Button
              color="primary"
              size="sm"
              className="rounded-pill"
              onClick={() => fetchTicketHistory(row.userId)}
            >
              <i className="mdi mdi-history me-1"></i>
              Activity
            </Button>
            <Button
              color="success"
              size="sm"
              className="rounded-pill"
              onClick={() => openMessageModal(row)}
              disabled={!row.contactNumber}
            >
              <i className="mdi mdi-message me-1"></i>
            </Button>
          </div>
        ),
      },
    ];

    return (
      <div className="mb-4">
        <Card className="shadow">
          <CardBody>
            <Row className="align-items-center filter-row">
              <Col md={2}>
                <FormGroup>
                  <Label for="searchText">User Name</Label>
                  <Input
                    type="text"
                    id="searchText"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search by user name..."
                    className=""
                  />
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label for="roleFilter">Filter by Role</Label>
                  <Input
                    type="select"
                    id="roleFilter"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="">All Roles</option>
                    {roles?.map((role) => (
                      <option key={role.roleId} value={role.roleId}>
                        {role.role}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label for="divisionFilter">Filter by Division</Label>
                  <Input
                    type="select"
                    id="divisionFilter"
                    value={divisionFilter}
                    onChange={(e) => setDivisionFilter(e.target.value)}
                  >
                    <option value="">All Divisions</option>
                    {divisions &&
                      divisions?.data?.map((division) => (
                        <option
                          key={division?.divisionId}
                          value={division?.divisionId}
                        >
                          {division?.divisionName}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label for="rangeFilter">Filter by Range</Label>
                  <Input
                    type="select"
                    id="rangeFilter"
                    value={rangeFilter}
                    onChange={(e) => setRangeFilter(e.target.value)}
                    disabled={!divisionFilter} // Disable if no division selected
                  >
                    <option value="">All Ranges</option>
                    {divisionFilter &&
                      ranges?.data?.map((range) => (
                        <option key={range.rangeId} value={range.rangeId}>
                          {range.rangeName} {range.name}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2}>
                <FormGroup>
                  <Label for="roundFilter">Filter by Round</Label>
                  <Input
                    type="select"
                    id="roundFilter"
                    value={roundFilter}
                    onChange={(e) => setRoundFilter(e.target.value)}
                    disabled={!rangeFilter} // Disable if no range selected
                  >
                    <option value="">All Rounds</option>
                    {rangeFilter &&
                      rounds?.data?.map((round) => (
                        <option key={round.roundId} value={round.roundId}>
                          {round.roundName} {round.name}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={2} className="mt-3">
                <div className="d-flex gap-2 flex-column">
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleFilterApply}
                      disabled={loading}
                    >
                      Apply
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={handleResetFilters}
                      disabled={loading}
                    >
                      Reset
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => openMessageModal()}
                      disabled={selectedUsers.length === 0}
                    >
                      <i className="mdi mdi-message-text me-1"></i>
                      Send ({selectedUsers.length})
                    </Button>
                  </div>
                  <Button
                    color="info"
                    size="sm"
                    onClick={handleExportData}
                    disabled={filteredData.length === 0 || loading}
                  >
                    <i className="mdi mdi-download me-1"></i>
                    Export CSV
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="p-4 text-center">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading data...</p>
            </div>
          }
          noDataComponent={
            <div className="p-4 text-center">
              <i
                className="mdi mdi-information-outline text-muted"
                style={{ fontSize: "2rem" }}
              ></i>
              <p className="mt-2">No users found matching your criteria</p>
            </div>
          }
          highlightOnHover
          pointerOnHover
          striped
        />

        {/* Message Modal */}
        <Modal
          isOpen={showMessageModal}
          toggle={() => setShowMessageModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowMessageModal(false)}>
            {singleUser
              ? `Send Message to ${singleUser.firstName} ${singleUser.lastName}`
              : `Send Message to ${selectedUsers.length} Users`}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="messageType">Message Type</Label>
                  <Input
                    type="select"
                    id="messageType"
                    value={messageType}
                    onChange={(e) => setMessageType(e.target.value)}
                  >
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms">SMS</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="messageText">Message</Label>
              <Input
                type="textarea"
                id="messageText"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter your message here..."
                rows="4"
              />
            </FormGroup>

            {singleUser ? (
              <div className="border rounded p-3 mb-3">
                <h6>Recipient:</h6>
                <p className="mb-1">
                  <strong>
                    {singleUser.firstName} {singleUser.lastName}
                  </strong>
                </p>
                <p className="mb-0 text-muted">
                  <i className="mdi mdi-phone me-1"></i>
                  {singleUser.contactNumber
                    ? maskIndianPhoneNumber(
                        String(singleUser.contactNumber).replace(/\D/g, "")
                      )
                    : "No phone number"}
                </p>
              </div>
            ) : (
              <div className="border rounded p-3 mb-3">
                <h6>Recipients ({selectedUsers.length}):</h6>
                <div className="max-height-200 overflow-auto">
                  {/* {filteredData
                  .filter(user => selectedUsers.includes(user.userId))
                  .map(user => (
                    <div key={user.userId} className="d-flex justify-content-between align-items-center py-1">
                      <span>{user.firstName} {user.lastName}</span>
                      <span className="text-muted">
                        {user.contactNumber ? maskIndianPhoneNumber(String(user.contactNumber).replace(/\D/g, "")) : 'No phone'}
                      </span>
                    </div>
                  ))} */}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setShowMessageModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="success"
              onClick={sendMessage}
              disabled={!messageText.trim()}
            >
              <i
                className={`mdi ${
                  messageType === "whatsapp" ? "mdi-whatsapp" : "mdi-message"
                } me-1`}
              ></i>
              Send {messageType === "whatsapp" ? "WhatsApp" : "SMS"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  };
  

  // Render ticket history
  const renderTicketHistory = () => {
    const user = allUsersnvehicle?.find((u) => u.userId === selectedTicket);

    // Sort ticket history by timestamp
    const historyToDisplay = ticketHistory?.length
      ? [...ticketHistory].sort((a, b) => {
          const timeA = a.updatedAt || a.createdAt || 0;
          const timeB = b.updatedAt || b.createdAt || 0;
          return new Date(timeB) - new Date(timeA); // Newest first
        })
      : [];

    return (
      <div className="">
        <Card className="shadow">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Button
                color="outline-primary"
                className="d-flex align-items-center rounded-pill"
                onClick={() => setSelectedTicket(null)}
              >
                <i className="mdi mdi-arrow-left me-1"></i>
                Back to Users
              </Button>
              <h2 className="card-title mb-0">User Activity</h2>
              <div></div>
            </div>

            {user && (
              <div className="bg-white p-4 border rounded mb-4 shadow-sm">
                <Row>
                  <Col md={2} className="text-center">
                    <div className="avatar avatar-md bg-primary rounded-circle mx-auto mb-2">
                      <span
                        className="avatar-text"
                        style={{ fontSize: "24px" }}
                      >
                        {user.firstName?.charAt(0).toUpperCase() || ""}
                        {user.lastName?.charAt(0).toUpperCase() || ""}
                      </span>
                    </div>
                  </Col>
                  <Col md={10}>
                    <Row>
                      <Col md={6}>
                        <h4 className="text-capitalize mb-3">
                          {user.firstName?.trim().charAt(0).toUpperCase() +
                            (user.firstName?.slice(1) || "")}{" "}
                          {user.lastName?.trim().charAt(0).toUpperCase() +
                            (user.lastName?.slice(1) || "")}
                        </h4>
                        <p className="mb-2">
                          <i className="mdi mdi-email-outline me-2 text-muted"></i>
                          {user.email || "No email"}
                        </p>
                        <p className="mb-2">
                          <i className="mdi mdi-phone me-2 text-muted"></i>
                          {user.contactNumber
                            ? maskIndianPhoneNumber(
                                String(user.contactNumber).replace(/\D/g, "")
                              )
                            : "No phone"}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <i className="mdi mdi-briefcase me-2 text-muted"></i>
                          {(() => {
                            const roleId =
                              user["regionId#roleId#userId"]?.split("#")[1];
                            const userRole = roles?.find(
                              (role) => role.roleId == roleId
                            );
                            return userRole ? (
                              <span className="text-capitalize">
                                {userRole.role}
                              </span>
                            ) : (
                              "No role"
                            );
                          })()}
                        </p>
                        <p className="mb-2">
                          <i className="mdi mdi-office-building me-2 text-muted"></i>
                          <span className="text-capitalize">
                            {user.divisionName || "No division"}
                          </span>
                        </p>
                        <p className="mb-2">
                          <i className="mdi mdi-map-marker me-2 text-muted"></i>
                          <Badge
                            color={
                              user.info?.currentLocation?.isLocationOn
                                ? "success"
                                : "danger"
                            }
                            className="rounded-pill"
                          >
                            {user.info?.currentLocation?.isLocationOn
                              ? "On"
                              : "Off"}
                          </Badge>
                          <span className="ms-2">
                            {formatDateOnly(
                              user.info?.currentLocation?.updatedAt
                            )}
                          </span>
                        </p>
                        <p className="mb-2">
                          <span>
                            <i className={`mdi mdi-laptop me-2`}></i>
                            {user?.deviceInfo ? (
                              <>
                                <span>
                                  {user?.deviceInfo?.os}{" "}
                                  <em>
                                    ({user?.deviceInfo?.brand}{" "}
                                    {user?.deviceInfo?.versionDevice})
                                  </em>
                                </span>
                              </>
                            ) : (
                              "-"
                            )}
                          </span>
                        </p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )}

            <div className="timeline-container">
              {activityLoading ? (
                <div className="text-center p-5">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading user activity...</p>
                </div>
              ) : historyToDisplay?.length > 0 ? (
                historyToDisplay.map((actionData, index) => {
                  const isLast = index === historyToDisplay.length - 1;

                  const displayTimestamp =
                    actionData.updatedAt || actionData.createdAt;
                  const actionUser = actionData.updatedBy
                    ? actionData.updatedBy
                    : actionData.createdBy
                    ? actionData.createdBy
                    : "Unknown";

                  const getStatusColor = (status) => {
                    if (!status) return "bg-light";
                    switch (status.toLowerCase()) {
                      case "open":
                        return "bg-info";
                      case "in progress":
                        return "bg-warning";
                      case "resolved":
                        return "bg-success";
                      case "closed":
                        return "bg-secondary";
                      default:
                        return "bg-light";
                    }
                  };

                  return (
                    <div className="timeline-item">
                      <div className="timeline-line-wrap">
                        <div className="timeline-line"></div>
                        <div
                          className={`timeline-dot ${getStatusColor(
                            actionData.status
                          )}`}
                        ></div>
                        {!isLast && <div className="timeline-line"></div>}
                      </div>
                      <div className="timeline-content">
                        <div className="border rounded shadow-sm bg-white">
                          <div className="p-3">
                            <div className="d-flex justify-content-between mb-2">
                              <h5 className="mb-0 font-size-15">
                                <span
                                  className={`badge bg-${getStatusBadge(
                                    actionData.status
                                  )}`}
                                >
                                  {actionData.status || ""}
                                </span>
                                {actionData.serialId && (
                                  <span className="ms-2 text-muted">
                                    {actionData.serialId}
                                  </span>
                                )}
                              </h5>
                              {actionData.ticketId && (
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="rounded-pill"
                                  onClick={(e) =>
                                    goToTicketDetails(e, actionData.ticketId)
                                  }
                                >
                                  <i className="mdi mdi-arrow-right me-1"></i>
                                  Go to Ticket
                                </Button>
                              )}
                            </div>

                            <div className="text-muted d-flex gap-3 flex-wrap mt-2">
                              {actionData.actions && (
                                <div>
                                  <i className="mdi mdi-list-status me-1"></i>
                                  <span className="text-capitalize">
                                    {actionData.actions}
                                  </span>
                                </div>
                              )}
                              <div>
                                <i className="mdi mdi-calendar-clock me-1"></i>
                                {formatDateOnly(displayTimestamp)}
                              </div>
                              <div>
                                <i className="mdi mdi-account me-1"></i>
                                {(() => {
                                  const user = allUsersnvehicle?.find(
                                    (user) => user.userId === actionUser
                                  );
                                  return user ? (
                                    <span className="text-capitalize">{`${
                                      user.firstName || ""
                                    } ${user.lastName || ""}`}</span>
                                  ) : (
                                    actionUser
                                  );
                                })()}
                              </div>
                            </div>

                            {actionData.comments && (
                              <div className="mt-2 border-top pt-2">
                                <p className="mb-0 text-muted font-size-13">
                                  <i className="mdi mdi-comment-text-outline me-1"></i>
                                  {actionData.comments}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-5">
                  <i
                    className="mdi mdi-information-outline text-muted"
                    style={{ fontSize: "2rem" }}
                  ></i>
                  <p className="mt-2">
                    No activity records found for this user.
                  </p>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Control Room" breadcrumbItem="User Logs" />
        {selectedTicket ? renderTicketHistory() : renderReportsTable()}
      </Container>

      {/* Custom CSS for styling */}
      <style jsx>{`
        .avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          color: white;
          font-weight: 600;
        }

        .avatar-md {
          width: 64px;
          height: 64px;
        }

        .avatar-text {
          font-size: 16px;
        }

        .timeline-container {
          position: relative;
          padding: 20px 0;
        }

        .timeline-item {
          display: flex;
          margin-bottom: 20px;
        }

        .timeline-line-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
        }

        .timeline-line {
          width: 2px;
          height: 20px;
          background-color: #e0e0e0;
        }

        .timeline-dot {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px #e0e0e0;
        }

        .timeline-content {
          flex: 1;
          padding-bottom: 15px;
        }

        .filter-row {
          background-color: #ffffff;
          padding: 15px;
        }

        .bg-info {
          background-color: #0dcaf0 !important;
        }

        .bg-warning {
          background-color: #ffc107 !important;
        }

        .bg-success {
          background-color: #198754 !important;
        }

        .bg-secondary {
          background-color: #6c757d !important;
        }
      `}</style>
    </div>
  );
}
